// src/components/QualifyForm.jsx
import React, { useState, useEffect, useRef } from 'react';
import useModalA11y from '../hooks/useModalA11y';


const generateHeightOptions = () => {
  const options = [];
  const minInches = 4 * 12;
  const maxInches = 7 * 12 + 6;
  for (let inches = minInches; inches <= maxInches; inches++) {
    const feet = Math.floor(inches / 12);
    const remainingInches = inches % 12;
    const display = `${feet}' ${remainingInches}"`;
    options.push({ value: inches.toString(), display });
  }
  return options;
};


// --- Component ---
function QualifyForm({ onSuccess, setLoading, setError, initialData }) {
  const heightOptions = generateHeightOptions();
  const defaultHeightInches = "69";

  const [formData, setFormData] = useState({
    height: initialData?.height || defaultHeightInches,
    weight: initialData?.weight || '',
    goal: initialData?.goal_weight || initialData?.goal || initialData?.goalWeight || '',
    gender: initialData?.gender || '',
  });
  const [bmi, setBmi] = useState(initialData?.bmi || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // State now controls the modal visibility
  const [showGoalDisclaimer, setShowGoalDisclaimer] = useState(false);
  const goalDisclaimerRef = useRef(null);

  useModalA11y({
    isOpen: showGoalDisclaimer,
    dialogRef: goalDisclaimerRef,
    onClose: () => setShowGoalDisclaimer(false),
  });

  // --- Effects (remain the same) ---
  useEffect(() => {
    const calculated = calculateBMI(formData.height, formData.weight);
    setBmi(calculated);
  }, [formData.height, formData.weight]);

  useEffect(() => {
    setFormData(prev => ({
      height: prev.height || initialData?.height || defaultHeightInches,
      weight: prev.weight || initialData?.weight || '',
      goal: prev.goal || initialData?.goal_weight || initialData?.goal || initialData?.goalWeight || '',
      gender: prev.gender || initialData?.gender || '',
    }));
    if (!formData.height && !formData.weight && initialData?.bmi) {
        setBmi(initialData.bmi);
    } else if (initialData?.height && initialData?.weight) {
        setBmi(calculateBMI(initialData.height, initialData.weight));
    } else if (formData.height && formData.weight) {
        setBmi(calculateBMI(formData.height, formData.weight));
    }
  }, [initialData]);

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['weight', 'goal'].includes(name)) {
        if (/^\d*\.?\d*$/.test(value)) {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  // --- Helper Functions (remain the same) ---
  const calculateBMI = (height, weight) => {
    const heightNum = parseFloat(height);
    const weightNum = parseFloat(weight);
    if (heightNum > 0 && weightNum > 0) {
      const bmi = (weightNum / (heightNum * heightNum)) * 703;
      return bmi.toFixed(1);
    }
    return '';
  };

  const determineQualification = (bmi, gender) => {
    const bmiNum = parseFloat(bmi);
    if (isNaN(bmiNum)) return false;
    if (gender === 'M') return bmiNum >= 27;
    if (gender === 'F') return bmiNum >= 25; // Changed from 'Female' to 'F'
    return false;
  };

  // NEW: Function containing the logic after validation and disclaimer check
  const proceedWithSubmission = () => {
    try {
      const calculatedBmi = calculateBMI(formData.height, formData.weight);
      const qualified = determineQualification(calculatedBmi, formData.gender);

      const successData = {
        height: formData.height,
        weight: formData.weight,
        goal: formData.goal,
        goal_weight: formData.goal,
        gender: formData.gender,
        bmi: calculatedBmi,
        qualified: qualified,
      };
      onSuccess(successData); // Call parent workflow handler

    } catch {
      setError('An unexpected error occurred. Please try again.');
      // Ensure loading state is reset even if error happens here
      setIsSubmitting(false);
      setLoading(false);
    } finally {
        // Loading/Submitting state is reset here *after* onSuccess is called
        // or if an error occurred during the final processing steps.
        // Note: setLoading(false) might already be called by the parent workflow
        // after onSuccess finishes its work, depending on its implementation.
        // If the parent handles it, you might remove setLoading(false) from here.
        setIsSubmitting(false);
        setLoading(false);
    }
  };

  // UPDATED: HandleSubmit now checks disclaimer and either proceeds or shows modal
  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    // Don't reset disclaimer here, it's reset on acknowledge
    setIsSubmitting(true);
    setLoading(true);

    // Basic Validation
    if (!formData.height || !formData.weight || !formData.goal || !formData.gender) {
      setError('Please fill in all fields.');
      setIsSubmitting(false);
      setLoading(false);
      return;
    }

    const heightNum = parseFloat(formData.height);
    const weightNum = parseFloat(formData.weight);
    const goalNum = parseFloat(formData.goal);

    if (isNaN(heightNum) || heightNum <= 0 || isNaN(weightNum) || weightNum <= 0 || isNaN(goalNum) || goalNum <= 0) {
        setError('Please enter valid numbers for weight and goal weight, and select a height.');
        setIsSubmitting(false);
        setLoading(false);
        return;
    }

    // Goal Weight Check
    const weightLossPercentage = ((weightNum - goalNum) / weightNum) * 100;

    if (weightLossPercentage > 30) {
      setShowGoalDisclaimer(true);
      setIsSubmitting(false);
      setLoading(false);
      return;
    }

    // Goal is within limits, proceed directly
    proceedWithSubmission();
  };

  // NEW: Handler for the modal's acknowledge button
  const handleAcknowledgeDisclaimer = () => {
    setShowGoalDisclaimer(false); // Hide the modal
    proceedWithSubmission(); // Now, continue with the submission process
  };


  // --- Render ---
  return (
    <> {/* Use Fragment to wrap form and modal */}
      <form onSubmit={handleSubmit} className="space-y-4">
        <h3 className="text-lg font-semibold text-primary mb-4">Qualification Check</h3>
        <p className="text-sm text-neutral-dark mb-4">Please provide the following information to help us determine if you might be a good candidate for our program.</p>

        {/* Gender Select */}
        <div>
          <label htmlFor="gender" className="block text-sm font-medium text-neutral-dark mb-1">Gender (sex at birth)</label>
          <select
            id="gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            className="w-full p-2 border rounded border-neutral bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            required
            disabled={isSubmitting}
          >
            <option value="" disabled>-- Select --</option>
            <option value="M">Male</option>
            <option value="F">Female</option>
          </select>
        </div>

        {/* Height Select */}
        <div>
          <label htmlFor="height" className="block text-sm font-medium text-neutral-dark mb-1">Height</label>
          <select
            id="height"
            name="height"
            value={formData.height}
            onChange={handleChange}
            className="w-full p-2 border rounded border-neutral bg-white focus:outline-none focus:ring-2 focus:ring-primary"
            required
            disabled={isSubmitting}
          >
            {heightOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.display}
              </option>
            ))}
          </select>
        </div>

        {/* Weight Input */}
        <div>
          <label htmlFor="weight" className="block text-sm font-medium text-neutral-dark mb-1">Current Weight (lbs)</label>
          <input
            id="weight"
            name="weight"
            type="text"
            inputMode="decimal"
            placeholder="e.g., 185"
            value={formData.weight}
            onChange={handleChange}
            className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
            required
            disabled={isSubmitting}
          />
        </div>

         {/* BMI Display */}
         <div>
          <label htmlFor="bmi" className="block text-sm font-medium text-neutral-dark mb-1">Calculated BMI</label>
          <input
            id="bmi"
            name="bmi"
            type="text"
            value={bmi || 'Select height & enter weight'}
            className="w-full p-2 border rounded border-neutral bg-neutral-light focus:outline-none"
            readOnly
            disabled={isSubmitting}
          />
        </div>

        {/* Goal Weight Input - Disclaimer text removed */}
        <div>
          <label htmlFor="goal" className="block text-sm font-medium text-neutral-dark mb-1">Goal Weight (lbs)</label>
          <input
            id="goal"
            name="goal"
            type="text"
            inputMode="decimal"
            placeholder="e.g., 160"
            value={formData.goal}
            onChange={handleChange}
            className="w-full p-2 border rounded border-neutral focus:outline-none focus:ring-2 focus:ring-primary"
            required
            disabled={isSubmitting}
          />
          {/* Inline disclaimer removed */}
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <button
            type="submit"
            className={`w-full bg-button text-white py-2 rounded-md hover:bg-button-light transition duration-200 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isSubmitting}
          >
            {/* Text changes based on isSubmitting state */}
            {isSubmitting ? 'Checking...' : 'Check Qualification & Continue'}
          </button>
        </div>
      </form>

      {/* --- NEW: Goal Disclaimer Modal --- */}
      {showGoalDisclaimer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
          <div
            ref={goalDisclaimerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="goalWeightNoticeTitle"
            tabIndex={-1}
            className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full max-h-[90vh] overflow-y-auto"
          >
            <h4 id="goalWeightNoticeTitle" className="text-lg font-semibold text-primary mb-3">Goal Weight Notice</h4>
            <p className="text-sm text-neutral-dark mb-4">
                Your goal weight is a powerful step toward transforming your health! With tirzepatide, patients typically achieve up to 30% loss of excess weight, while semaglutide supports up to 20%—both incredible milestones backed by clinical results. While some may lose more, these outcomes are less common, and aiming beyond these ranges may not be sustainable for most. We’re excited to partner with you and your RediMD healthcare provider to create a personalized plan with achievable targets, ensuring steady progress and lasting success.
            </p>
            <button
              type="button"
              onClick={handleAcknowledgeDisclaimer}
              className="w-full bg-button text-white py-2 px-4 rounded-md hover:bg-button-light transition duration-200"
            >
              Acknowledge & Continue
            </button>
          </div>
        </div>
      )}
      {/* --- End Modal --- */}
    </>
  );
}

export default QualifyForm;
